(() => {
  'use strict';
  if (customElements.get('bm-dome-transition')) return;

  class BmDomeTransition extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._frame = 0;
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._refresh = () => {
        this.syncSource();
        this.measure();
      };
      this._queue = () => {
        if (!this._frame) this._frame = requestAnimationFrame(() => {
          this._frame = 0;
          if (this.isConnected) this._refresh();
        });
      };
      // Scroll never writes layout, transforms, or clipping. The compositor
      // handles native sticky in either direction; only layout changes measure.
      window.addEventListener('resize', this._queue, { passive: true });
      window.addEventListener('pageshow', this._queue);
      this._motion.addEventListener('change', this._queue);
      document.addEventListener('shopify:section:load', this._queue);
      document.addEventListener('shopify:section:unload', this._queue);
      document.addEventListener('shopify:section:reorder', this._queue);
      this._resize = new ResizeObserver(this._queue);
      this._resize.observe(this);
      this._refresh();
    }

    syncSource() {
      const wrapper = this.closest('.shopify-section');
      const previous = wrapper && wrapper.previousElementSibling;
      const source = !this._motion.matches && previous && previous.querySelector('.bm-lens');
      if (source === this._source && wrapper === this._wrapper) return;
      this.releaseSource();
      const track = source && source.querySelector('bm-lens-zoom');
      const sticky = track && track.querySelector('.bm-lens__sticky');
      if (!sticky) return;
      this._source = source;
      this._track = track;
      this._sticky = sticky;
      this._wrapper = wrapper;
      this._oldMargin = wrapper.style.getPropertyValue('margin-top');
      this._oldMarginPriority = wrapper.style.getPropertyPriority('margin-top');
      this._baseMargin = parseFloat(getComputedStyle(wrapper).marginTop) || 0;
      this._oldExtension = track.getAttribute('data-bm-dome-extension');
      source.classList.add('bm-dome-source');
      wrapper.classList.add('bm-dome-incoming');
      this._resize.observe(sticky);
    }

    measure() {
      if (!this._source) return;
      const stickyHeight = this._sticky.getBoundingClientRect().height;
      const radius = getComputedStyle(this).borderTopLeftRadius.split(/\s+/);
      const depth = parseFloat(radius[radius.length - 1]) || 0;
      const incomingHeight = this.getBoundingClientRect().height;
      if (!stickyHeight || !incomingHeight) return;

      // Extend containment until the dome's corners have passed the viewport.
      // The equal negative margin keeps the incoming section at its original
      // document position. The lens progress method subtracts this extension.
      const extension = Math.max(0, Math.ceil(stickyHeight + depth + this._baseMargin + 2));
      const tailClip = Math.max(0, extension - incomingHeight - this._baseMargin);
      if (extension !== this._lastExtension) {
        this._source.style.setProperty('--bm-dome-extension', extension + 'px');
        this._track.setAttribute('data-bm-dome-extension', String(extension));
        this._wrapper.style.setProperty('margin-top', (this._baseMargin - extension) + 'px', this._oldMarginPriority);
        this._lastExtension = extension;
      }
      if (tailClip !== this._lastTailClip) {
        // A short incoming section must not expose the held lens through a
        // transparent following section. This crop is constant during scroll.
        this._source.style.setProperty('--bm-dome-tail-clip', tailClip.toFixed(3) + 'px');
        this._lastTailClip = tailClip;
      }
    }

    releaseSource() {
      if (this._source) {
        this._source.classList.remove('bm-dome-source');
        this._source.style.removeProperty('--bm-dome-extension');
        this._source.style.removeProperty('--bm-dome-tail-clip');
      }
      if (this._track) {
        if (this._oldExtension === null) this._track.removeAttribute('data-bm-dome-extension');
        else this._track.setAttribute('data-bm-dome-extension', this._oldExtension);
      }
      if (this._wrapper) {
        this._wrapper.classList.remove('bm-dome-incoming');
        if (this._oldMargin) this._wrapper.style.setProperty('margin-top', this._oldMargin, this._oldMarginPriority);
        else this._wrapper.style.removeProperty('margin-top');
      }
      if (this._resize && this._sticky) this._resize.unobserve(this._sticky);
      this._source = this._track = this._sticky = this._wrapper = null;
      this._lastExtension = this._lastTailClip = null;
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      cancelAnimationFrame(this._frame);
      this._frame = 0;
      window.removeEventListener('resize', this._queue);
      window.removeEventListener('pageshow', this._queue);
      this._motion.removeEventListener('change', this._queue);
      document.removeEventListener('shopify:section:load', this._queue);
      document.removeEventListener('shopify:section:unload', this._queue);
      document.removeEventListener('shopify:section:reorder', this._queue);
      this.releaseSource();
      this._resize.disconnect();
    }
  }

  customElements.define('bm-dome-transition', BmDomeTransition);
})();
